import { Box, Text, VStack } from '@chakra-ui/react'

import { FormModal } from '../../../components/FormModal'

interface IAuctionWhatsAppInfoModalProps {
  isOpen: boolean
  onClose: () => void
}

/**
 * Explica quando e o que a integração de WhatsApp envia durante o leilão.
 *
 * Conteúdo estático: vive num componente próprio porque o EventAdminPage já
 * passa de 2.300 linhas e delega seus outros modais da mesma forma.
 *
 * Sem `onSubmit` de propósito — é um modal de leitura, então o FormModal já
 * monta só o Fechar no rodapé.
 */
export default function AuctionWhatsAppInfoModal({ isOpen, onClose }: IAuctionWhatsAppInfoModalProps) {
  return (
    <FormModal isOpen={isOpen} onClose={onClose} title="Notificações por WhatsApp" size="default">
      <Text variant="caption" mb={4}>
        Com a chave ligada, este leilão envia duas mensagens. O visitante não precisa fazer nada para recebê-las. Basta
        ter um celular no cadastro.
      </Text>

      <VStack align="stretch" spacing={4}>
        <Box bg="gray.50" borderRadius="lg" p={4}>
          <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={1}>
            1. Quando o lance dele é superado
          </Text>
          <Text variant="caption" mb={3}>
            Sai assim que outra pessoa dá um lance maior em uma obra que ele estava ganhando. A mensagem traz o nome da
            obra e o valor do lance atual, e convida a dar um novo lance. Ela <b>não diz quem</b> deu o lance maior.
          </Text>
          <Box bg="white" borderRadius="md" border="1px" borderColor="gray.200" p={3}>
            <Text fontSize="xs" color="gray.700" whiteSpace="pre-line" fontFamily="mono">
              {
                'Olá, João Silva!\n\nSeu lance na obra *Paisagem ao Entardecer* foi superado.\n\nLance atual: *R$ 500,00*\n\nVocê ainda pode dar um novo lance.'
              }
            </Text>
          </Box>
        </Box>

        <Box bg="gray.50" borderRadius="lg" p={4}>
          <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={1}>
            2. Quando ele arremata
          </Text>
          <Text variant="caption" mb={3}>
            Sai no encerramento do leilão. É <b>uma única mensagem por pessoa</b>, listando todas as obras que ela levou
            e o total, com o lembrete de passar no caixa.
          </Text>
          <Box bg="white" borderRadius="md" border="1px" borderColor="gray.200" p={3}>
            <Text fontSize="xs" color="gray.700" whiteSpace="pre-line" fontFamily="mono">
              {
                'Parabéns, João Silva!\n\nVocê arrematou no leilão do Encontro de Arte:\n• *Paisagem ao Entardecer* — R$ 500,00\n• *Retrato em Azul* — R$ 350,00\n\nTotal: *R$ 850,00*'
              }
            </Text>
          </Box>
        </Box>

        <Box>
          <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={2}>
            Para não virar spam
          </Text>
          <VStack align="stretch" spacing={1}>
            <Text variant="caption">
              • No máximo <b>um aviso por obra a cada minuto</b> para a mesma pessoa. Se ela levar três lances por cima
              seguidos, recebe um aviso só, com o valor mais recente.
            </Text>
            <Text variant="caption">
              • No máximo <b>três mensagens por minuto</b> para o mesmo número, somando todas as obras.
            </Text>
          </VStack>
        </Box>

        <Box>
          <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={2}>
            Quem não recebe
          </Text>
          <VStack align="stretch" spacing={1}>
            <Text variant="caption">• Quem não tem celular no cadastro, ou tem apenas telefone fixo.</Text>
            <Text variant="caption">
              • Quem <b>voltou a liderar</b> a obra antes da mensagem sair.
            </Text>
            <Text variant="caption">• Quem aumentou o próprio lance. (Ninguém foi superado)</Text>
            <Text variant="caption">• No primeiro lance de uma obra, quando não havia ninguém para superar.</Text>
            <Text variant="caption">• Depois que o leilão encerra, o aviso de lance superado deixa de sair.</Text>
          </VStack>
        </Box>

        <Box bg="orange.50" borderRadius="lg" p={3}>
          <Text fontSize="xs" color="orange.700">
            Esta chave vale só para este leilão. O envio ainda depende da integração de WhatsApp estar ligada no
            servidor. Com ela desligada, nada sai mesmo com esta chave ligada.
          </Text>
        </Box>
      </VStack>
    </FormModal>
  )
}
